import React, { useState, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { Eye, EyeOff, Mail, Lock, Loader2, RotateCcw } from 'lucide-react'
import { Turnstile } from '@marsidev/react-turnstile'
import { trackSignIn, trackResendVerification } from '../../config/analyticsEvents'

const UNVERIFIED_MSG = 'Please check your email and click the verification link before signing in.'

const LoginForm = ({ onToggleForm, onSuccess, hasPlan }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [captchaToken, setCaptchaToken] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const turnstileRef = useRef()
  const captchaTokenRef = useRef('')

  const { signIn, resendVerificationEmail } = useAuth()

  const isUnverifiedError = error === UNVERIFIED_MSG

  const updateCaptchaToken = (token) => {
    setCaptchaToken(token)
    captchaTokenRef.current = token
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setResendMessage('')

    const { error } = await signIn(email, password, captchaToken, rememberMe)
    
    if (error) {
      setError(error.message)
      if (turnstileRef.current) {
        turnstileRef.current.reset()
        updateCaptchaToken('')
      }
    } else {
      trackSignIn('email')
      onSuccess?.()
    }
    
    setLoading(false)
  }

  const handleResendVerification = async () => {
    if (!email) return
    setResendLoading(true)
    setResendMessage('')

    if (turnstileRef.current) {
      turnstileRef.current.reset()
      updateCaptchaToken('')
    }

    await new Promise((resolve) => {
      const check = setInterval(() => {
        if (captchaTokenRef.current) {
          clearInterval(check)
          resolve()
        }
      }, 200)
      setTimeout(() => { clearInterval(check); resolve() }, 10000)
    })

    const token = captchaTokenRef.current
    if (!token) {
      setResendMessage('Please complete the CAPTCHA verification first.')
      setResendLoading(false)
      return
    }

    try {
      const { error } = await resendVerificationEmail(email, token)
      if (error) {
        setResendMessage(error.message)
      } else {
        trackResendVerification()
        setResendMessage('Verification email sent! Check your inbox.')
      }
    } catch {
      setResendMessage('Failed to resend. Please try again.')
    }

    if (turnstileRef.current) {
      turnstileRef.current.reset()
      updateCaptchaToken('')
    }
    setResendLoading(false)
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-400/30 via-indigo-400/30 to-cyan-400/30 rounded-3xl blur-2xl -z-10" />
        <div className="glass-effect bg-white rounded-2xl shadow-soft hover:shadow-hover transition-shadow p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 mb-2">
              {hasPlan ? 'Sign in to continue' : 'Welcome back'}
            </h2>
            <p className="text-gray-600">
              {hasPlan ? 'Access your account to get started' : 'Continue your development journey'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" aria-live="polite">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">
                Email address
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-blue-500 transition-colors" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-gray-50/50 hover:bg-white focus:bg-white shadow-sm"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-3">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-blue-500 transition-colors" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-gray-50/50 hover:bg-white focus:bg-white shadow-sm"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 select-none cursor-pointer group">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => onToggleForm('forgot')}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors hover:underline"
              >
                Forgot password?
              </button>
            </div>

            {/* Cloudflare Turnstile CAPTCHA */}
            <div className="w-full overflow-hidden pr-[4px] py-1">
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                  onSuccess={(token) => updateCaptchaToken(token)}
                  onError={() => {
                    updateCaptchaToken('')
                    setError('CAPTCHA verification failed. Please try again.')
                  }}
                  onExpire={() => {
                    updateCaptchaToken('')
                    setError('CAPTCHA expired. Please verify again.')
                  }}
                  options={{
                    theme: 'light',
                    size: 'flexible'
                  }}
                />
              </div>
            </div>

            {error && (
              <div className={`px-4 py-3 rounded-xl text-sm ${
                isUnverifiedError 
                  ? 'bg-amber-50 border border-amber-200 text-amber-700' 
                  : 'bg-red-50 border border-red-200 text-red-600'
              }`}>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
                {isUnverifiedError && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                      className="w-full flex items-center justify-center space-x-2 text-amber-800 hover:text-amber-900 font-medium py-2 px-3 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <RotateCcw className="h-4 w-4" />
                          <span>Resend verification email</span>
                        </>
                      )}
                    </button>
                    {resendMessage && (
                      <p className={`mt-2 text-xs text-center ${
                        resendMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {resendMessage}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !captchaToken}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="text-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">New to EasyAppIcon?</span>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => onToggleForm('register')}
                className="w-full border-2 border-blue-200 text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:border-blue-300"
              >
                Create new account
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
